import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import cleanDeep from 'clean-deep';
import { useContext } from 'react';

import { ApplicationContext } from '../../../../../shared/source/context/context';
import {
    certificateHistoryItemArraySchema,
    certificateHistoryItemSchema,
} from '../../../../../shared/source/data-contract/certificate-history';
import { getUserHardSoftSkillsQueryKey } from '../../../../../shared/source/query-hooks/hard-soft-skills';
import { nullifyEmptyStringValues } from '../../common/form';

import type {
    CertificateHistoryCreation,
    CertificateHistoryItem,
    CertificateHistoryMutation,
} from '../../../../../shared/source/data-contract/certificate-history';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

export const useCertificateHistoryQuery = (
    userId: string,
    enabled: boolean,
): UseQueryResult<readonly CertificateHistoryItem[]> => {
    const {
        applicationState: { language },
        validatingCobrainerApiFetcher,
    } = useContext(ApplicationContext);
    const certificateHistoryUrl = `/users/${userId}/certificate-history` as const;

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(certificateHistoryItemArraySchema, {
                pathname: certificateHistoryUrl,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [certificateHistoryUrl, 'GET', language.code],
    });
};

export const useOneCertificateHistoryQuery = (
    userId: string,
    certificateHistoryId: string,
    enabled: boolean,
): UseQueryResult<CertificateHistoryItem> => {
    const {
        applicationState: { language },
        validatingCobrainerApiFetcher,
    } = useContext(ApplicationContext);
    const oneCertificateHistoryUrl = `/users/${userId}/certificate-history/${certificateHistoryId}` as const;

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(certificateHistoryItemSchema, {
                pathname: oneCertificateHistoryUrl,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [oneCertificateHistoryUrl, 'GET', language.code],
    });
};

export type CertificateHistoryMutations = {
    readonly createCertificateMutation: UseMutationResult<
        CertificateHistoryItem,
        unknown,
        Omit<CertificateHistoryItem, 'id'>
    >;
    readonly deleteCertificateMutation: UseMutationResult<void, unknown, string>;
    readonly patchCertificateMutation: UseMutationResult<CertificateHistoryItem, unknown, CertificateHistoryItem>;
};

export const useCertificateHistoryMutations = (userId: string): CertificateHistoryMutations => {
    const queryClient = useQueryClient();
    const { cobrainerApiFetcher, validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    const invalidateCertificateCache = async (certificateHistoryId: string): Promise<void> => {
        await queryClient.invalidateQueries({
            queryKey: [`/users/${userId}/certificate-history/${certificateHistoryId}`, 'GET'],
        });
        await queryClient.invalidateQueries({ queryKey: [`/users/${userId}/certificate-history`, 'GET'] });
        await queryClient.invalidateQueries({ queryKey: getUserHardSoftSkillsQueryKey(userId) });
    };

    const createCertificateMutation = useMutation({
        mutationFn: async (values: Omit<CertificateHistoryItem, 'id'>): Promise<CertificateHistoryItem> => {
            const { skill, ...restValues } = values;
            const data: CertificateHistoryCreation = {
                ...cleanDeep(restValues),
                certificateName: values.certificateName,
                skillId: skill?.id ?? null,
            };

            const result = await validatingCobrainerApiFetcher.postJson(certificateHistoryItemSchema, {
                pathname: `/users/${userId}/certificate-history`,
                payload: data,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: (values: CertificateHistoryItem) => invalidateCertificateCache(values.id),
    });

    const patchCertificateMutation = useMutation({
        mutationFn: async (values: CertificateHistoryItem): Promise<CertificateHistoryItem> => {
            const { id, certificateName, skill, ...restValues } = values;
            const data: CertificateHistoryMutation = {
                ...cleanDeep(nullifyEmptyStringValues(restValues), { nullValues: false }),
                id,
                certificateName,
                skillId: skill?.id ?? null,
            };

            const result = await validatingCobrainerApiFetcher.patchJson(certificateHistoryItemSchema, {
                pathname: `/users/${userId}/certificate-history/${values.id}`,
                payload: data,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: (_, values) => invalidateCertificateCache(values.id),
    });

    const deleteCertificateMutation: UseMutationResult<void, unknown, string> = useMutation({
        mutationFn: async (id: string) => {
            const result = await cobrainerApiFetcher.delete({
                pathname: `/users/${userId}/certificate-history/${id}`,
            });

            if (result.isOk) {
                return;
            }

            throw result.error;
        },
        onSuccess: (_, id) => invalidateCertificateCache(id),
    });

    return {
        createCertificateMutation,
        deleteCertificateMutation,
        patchCertificateMutation,
    };
};
